/**
 * skeletons.tsx — page-shaped loading COMPOSITIONS (UX Wave 6 §1).
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: UX Wave 6 (loaders).
 *
 * WHY: Wave 6 kills the bare centred `<Spinner>` for in-portal STAFF loads —
 * a spinner says nothing about what is coming and causes a layout jump when
 * the real content lands. These compositions mirror the page shape instead:
 *   • BoardSkeleton  — lane headers + card blocks (the deals Pipeline and the
 *     Payments money board);
 *   • TableSkeleton  — a header row + data rows (users / audit / insights /
 *     payments-list tables);
 *   • DrawerSkeleton — stacked section blocks (the deal drawer, the
 *     subscription drill-down, in-drawer panels).
 * All three build on the ONE `ui/Skeleton` primitive, so reduced-motion
 * safety (static block, no shimmer) is inherited — never re-implemented.
 *
 * ACCESSIBILITY: each composition announces exactly ONE busy region —
 * `role="status"` + `aria-busy="true"` with an sr-only label — while every
 * visual block stays `aria-hidden` (the primitive's contract). A screen
 * reader hears "Loading the pipeline…", never a stutter of empty blocks.
 *
 * The tiny inline `<Spinner>` remains for small in-place waits (a button's
 * "…", a nested feed's next page) — see ui/Skeleton.tsx's doc block.
 */
import type { ReactNode } from 'react';
import { Skeleton } from './Skeleton';

/** The ONE busy-region wrapper every composition announces itself through. */
function BusyRegion({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div role="status" aria-busy="true">
      <span className="gps-sr-only">{label}</span>
      {children}
    </div>
  );
}

/**
 * A pipeline-board skeleton: `lanes` columns, each a lane-header bar over
 * `cardsPerLane` card blocks. Overflowing lanes are clipped (the real boards
 * scroll; a loading state must not).
 */
export function BoardSkeleton({
  lanes = 4,
  cardsPerLane = 2,
  label = 'Loading the board…',
}: {
  lanes?: number;
  cardsPerLane?: number;
  label?: string;
}) {
  return (
    <BusyRegion label={label}>
      <div
        aria-hidden="true"
        style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${lanes}, minmax(210px, 1fr))`,
          gap: 'var(--gps-s-3)',
          alignItems: 'start',
          overflow: 'hidden',
          padding: 'var(--gps-s-2) 0',
        }}
      >
        {Array.from({ length: lanes }).map((_, lane) => (
          // Static list (fixed length, never reordered) — the index is a stable key.
          <div key={lane} data-testid="board-skeleton-lane" style={{ display: 'grid', gap: 'var(--gps-s-3)', minWidth: 0 }}>
            <Skeleton width="60%" height="1.05rem" />
            {Array.from({ length: cardsPerLane }).map((_, card) => (
              <div
                key={card}
                style={{
                  border: '1px solid var(--gps-border)',
                  borderRadius: '8px',
                  padding: '10px',
                  display: 'grid',
                  gap: '8px',
                  background: 'var(--gps-surface)',
                }}
              >
                <Skeleton width="70%" height="0.95rem" />
                <Skeleton height="0.8rem" />
                <Skeleton width="45%" height="0.8rem" />
              </div>
            ))}
          </div>
        ))}
      </div>
    </BusyRegion>
  );
}

/** One row of column-width blocks — shared by the table's header and body rows. */
function TableSkeletonRow({ columns, header, testId }: { columns: number; header?: boolean; testId?: string }) {
  return (
    <div
      data-testid={testId}
      style={{ display: 'grid', gridTemplateColumns: `repeat(${columns}, 1fr)`, gap: 'var(--gps-s-3)' }}
    >
      {Array.from({ length: columns }).map((_, i) => (
        <Skeleton key={i} width={header ? '55%' : '85%'} height={header ? '0.8rem' : '0.95rem'} />
      ))}
    </div>
  );
}

/** A data-table skeleton: a header row of short blocks over `rows` data rows. */
export function TableSkeleton({
  rows = 5,
  columns = 5,
  label = 'Loading the table…',
}: {
  rows?: number;
  columns?: number;
  label?: string;
}) {
  return (
    <BusyRegion label={label}>
      <div aria-hidden="true" style={{ display: 'grid', gap: '12px', padding: 'var(--gps-s-2) 0' }}>
        <TableSkeletonRow columns={columns} header />
        {Array.from({ length: rows }).map((_, row) => (
          <TableSkeletonRow key={row} columns={columns} testId="table-skeleton-row" />
        ))}
      </div>
    </BusyRegion>
  );
}

/**
 * A drawer/detail skeleton: `blocks` stacked sections, each a section-title
 * bar over two content lines — the shape of the drill-down panels (drawer
 * chrome such as the real title stays with the caller; this is content-only).
 */
export function DrawerSkeleton({ blocks = 3, label = 'Loading the details…' }: { blocks?: number; label?: string }) {
  return (
    <BusyRegion label={label}>
      <div aria-hidden="true" style={{ display: 'grid', gap: 'var(--gps-s-4)', padding: 'var(--gps-s-2) 0' }}>
        {Array.from({ length: blocks }).map((_, block) => (
          <div key={block} data-testid="drawer-skeleton-block" style={{ display: 'grid', gap: '8px' }}>
            <Skeleton width="35%" height="0.95rem" />
            <Skeleton height="0.85rem" />
            <Skeleton width="80%" height="0.85rem" />
          </div>
        ))}
      </div>
    </BusyRegion>
  );
}
